import {
  action,
  comparer,
  computed,
  observable,
  makeObservable,
  reaction,
  toJS,
  type IReactionDisposer,
} from 'mobx'
import { z } from 'zod'
import StorageManager from 'services/StorageManager'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { isClient } from 'utils/runtimeUtils'
import {
  CART_SCHEMA_VERSION,
  CART_STORAGE_KEY,
  CartItemSchema,
  CartStorageSchema,
  type CartItem,
  type CartStorage,
  type PhotoBookCartItem,
  type TilesCartItem,
} from './CartItem'
import type {
  CartCheckoutPayload,
  CartItemAdapter,
  CartItemAdapterMap,
  PricingPayload,
  ReactiveCartItemAdapter,
} from './adapters/CartItemAdapter.types'
import { adapterFor } from './adapters/CartItemAdapter.utils'
import { getCartItemOrderKey } from './cartOrderKey'

const CART_STORAGE_ENVELOPE_SCHEMA = CartStorageSchema.omit({
  items: true,
}).extend({
  items: z.array(z.unknown()),
})

export class CartStore {
  @observable accessor cartItems: CartItem[] = []

  // Server-issued id shared across all useGroupCheckoutPricing() callers so sequential pricing
  // calls reuse one Checkout doc. Intentionally NOT observable — it's read in a non-reactive
  // react-query queryFn and must not trigger renders.
  groupCheckoutId: string | undefined = undefined

  private readonly adapters: CartItemAdapterMap

  private disposers: IReactionDisposer[] = []

  constructor(adapters: CartItemAdapterMap) {
    this.adapters = adapters

    if (isClient()) {
      const didRestore = this.restoreStoredItems()
      if (!didRestore) {
        this.forceLoadReactiveProducts()
      }

      this.setupProductSync()
      this.setupPersistence()
    }

    makeObservable(this)
  }

  @computed
  get tilesItem(): TilesCartItem | undefined {
    return this.cartItems.find(
      (item): item is TilesCartItem =>
        item.productType === PRODUCT_TYPES.CLASSIC
    )
  }

  @computed
  get photoBookItems(): PhotoBookCartItem[] {
    return this.cartItems.filter(
      (item): item is PhotoBookCartItem =>
        item.productType === PRODUCT_TYPES.PHOTO_BOOK
    )
  }

  getPricingPayloads(): PricingPayload[] {
    return mergePricingPayloadsByOrderKey(
      this.cartItems.map((item) => ({
        orderKey: getCartItemOrderKey(item),
        payload: adapterFor(this.adapters, item).buildPricingPayload(item),
      }))
    )
  }

  buildCheckoutPayloads(): CartCheckoutPayload[] {
    return mergeCheckoutPayloadsByOrderKey(
      this.cartItems.map((item) => {
        const adapter = adapterFor(this.adapters, item)
        return {
          orderKey: getCartItemOrderKey(item),
          payload: {
            ...adapter.buildPricingPayload(item),
            ...adapter.buildFulfillmentPayload(item),
          },
        }
      })
    )
  }

  @action
  syncProductItems<TItem extends CartItem>(
    productType: TItem['productType'],
    items: TItem[]
  ): void {
    // The adapter hands its items in the order the product itself is shown in,
    // so each one carries the sibling it follows: a book that sorts into the
    // middle belongs beside its neighbors, not below the rows added after it.
    let precedingItemId: string | undefined
    for (const item of items) {
      precedingItemId =
        this.addItem(item, precedingItemId)?.id ?? precedingItemId
    }

    const syncedIds = new Set(items.map((item) => item.id))
    const hasStaleItem = this.cartItems.some(
      (cartItem) =>
        cartItem.productType === productType && !syncedIds.has(cartItem.id)
    )
    if (hasStaleItem) {
      this.cartItems = this.cartItems.filter(
        (cartItem) =>
          cartItem.productType !== productType || syncedIds.has(cartItem.id)
      )
    }
  }

  @action
  addItem<TItem extends CartItem>(
    item: TItem,
    precedingItemId?: string
  ): TItem | null {
    const adapter = adapterFor(this.adapters, item)
    if (!adapter.hasBackingData(item)) return null

    const existingIndex = this.cartItems.findIndex(
      (cartItem) => cartItem.id === item.id
    )

    if (existingIndex === -1) {
      this.cartItems.splice(
        getInsertIndexForItem(this.cartItems, item, precedingItemId),
        0,
        item
      )
    } else {
      if (isSameCartItemContent(this.cartItems[existingIndex], item)) {
        return item
      }
      const previousDismissed =
        this.cartItems[existingIndex].dismissedWarningTypes
      this.cartItems[existingIndex] = item
      this.cartItems[existingIndex].dismissedWarningTypes = previousDismissed
    }

    return item
  }

  @action
  setItemQuantity(id: string, quantity: number): void {
    const item = this.cartItems.find((item) => item.id === id)
    if (!item) return
    item.quantity = Math.max(1, quantity)
  }

  @action
  removeItem(id: string): void {
    const item = this.cartItems.find((cartItem) => cartItem.id === id)
    if (!item) return

    adapterFor(this.adapters, item).clearBackingData(item)
    this.cartItems = this.cartItems.filter((cartItem) => cartItem.id !== id)
  }

  @action
  dismissWarning(itemId: string, warningType: string): void {
    const item = this.cartItems.find((cartItem) => cartItem.id === itemId)
    if (!item) return
    const current = item.dismissedWarningTypes ?? []
    if (current.includes(warningType)) return
    item.dismissedWarningTypes = [...current, warningType]
  }

  @action
  clearAll(): void {
    for (const item of [...this.cartItems]) {
      adapterFor(this.adapters, item).clearBackingData(item)
    }
    this.cartItems = []
    this.groupCheckoutId = undefined
  }

  clearGroupCheckoutId(): void {
    this.groupCheckoutId = undefined
  }

  dispose(): void {
    this.disposers.forEach((dispose) => dispose())
    this.disposers = []
  }

  @action
  private restoreStoredItems(): boolean {
    if (StorageManager.hasKey(CART_STORAGE_KEY)) {
      this.cartItems = this.readStoredItems()
      return true
    }

    return false
  }

  private readStoredItems(): CartItem[] {
    const storedCart = StorageManager.get(CART_STORAGE_KEY)
    const parsedStorage = CartStorageSchema.safeParse(storedCart)
    const rawItems = parsedStorage.success
      ? parsedStorage.data.items
      : this.readValidItemsFromInvalidStorage(storedCart)

    return rawItems.filter((item) =>
      adapterFor(this.adapters, item).hasBackingData(item)
    )
  }

  private readValidItemsFromInvalidStorage(storedCart: unknown): CartItem[] {
    const envelope = CART_STORAGE_ENVELOPE_SCHEMA.safeParse(storedCart)
    if (!envelope.success) return []

    return envelope.data.items
      .map((item) => CartItemSchema.safeParse(item))
      .filter(
        (result): result is { success: true; data: CartItem } => result.success
      )
      .map((result) => result.data)
  }

  private getAdapters(): CartItemAdapter[] {
    return Object.values(this.adapters) as CartItemAdapter[]
  }

  private forceLoadReactiveProducts(): void {
    this.getAdapters()
      .filter(
        (adapter): adapter is ReactiveCartItemAdapter =>
          adapter.kind === 'reactive'
      )
      .forEach((adapter) => adapter.forceProductLoad())
  }

  private setupProductSync(): void {
    this.getAdapters().forEach((adapter) => {
      if (adapter.kind !== 'reactive') return
      this.disposers.push(this.setupAdapterProductSync(adapter))
    })
  }

  private setupAdapterProductSync(
    adapter: ReactiveCartItemAdapter
  ): IReactionDisposer {
    return reaction(
      () => adapter.getCartItemsFromLoadedProduct(),
      (result) => {
        if (result.status === 'not-loaded') return
        this.syncProductItems(
          adapter.productType,
          result.status === 'existing' ? result.items : []
        )
      },
      {
        fireImmediately: true,
        // The adapters deep-read stores that are mutated in place per
        // keystroke (a book caption, a tile edit); the delay batches the
        // bursts, and addItem drops rebuilds whose content didn't change,
        // so cartItems isn't rewritten once per character.
        delay: 150,
        name: `CartStore:sync:${adapter.productType}`,
      }
    )
  }

  private setupPersistence(): void {
    this.disposers.push(
      reaction(
        () => toJS(this.cartItems),
        (cartItems) => this.persist(cartItems),
        { name: 'CartStore:persist', delay: 150, fireImmediately: true }
      )
    )
  }

  private persist(cartItems: CartItem[]): void {
    if (!isClient()) return
    StorageManager.set<CartStorage>(CART_STORAGE_KEY, {
      schemaVersion: CART_SCHEMA_VERSION,
      items: cartItems,
      updatedAt: Date.now(),
    })
  }
}

// Lands the item right after the sibling it follows, and before the first one of
// its product type when it follows none. A cart holding several product types
// otherwise sends every new item to the bottom, past rows it belongs above.
function getInsertIndexForItem(
  cartItems: CartItem[],
  item: CartItem,
  precedingItemId: string | undefined
): number {
  const precedingIndex = cartItems.findIndex(
    (cartItem) => cartItem.id === precedingItemId
  )
  if (precedingIndex !== -1) return precedingIndex + 1

  const firstSiblingIndex = cartItems.findIndex(
    (cartItem) => cartItem.productType === item.productType
  )
  return firstSiblingIndex === -1 ? cartItems.length : firstSiblingIndex
}

type OrderKeyedPayload<TPayload> = {
  orderKey: string
  payload: TPayload
}

// Items sharing an order key become one order. The backend computes bulk
// discounts per order over that order's own items, so items of a product type
// share one order to price as N units of one; a photo book is the exception —
// an order's draft records a single book, so each book needs its own order.
function mergePricingPayloadsByOrderKey(
  entries: OrderKeyedPayload<PricingPayload>[]
): PricingPayload[] {
  const mergedByOrderKey = new Map<string, PricingPayload>()
  for (const { orderKey, payload } of entries) {
    const merged = mergedByOrderKey.get(orderKey)
    if (!merged) {
      mergedByOrderKey.set(orderKey, {
        productType: payload.productType,
        products: [...payload.products],
      })
    } else {
      merged.products.push(...payload.products)
    }
  }
  return [...mergedByOrderKey.values()]
}

function mergeCheckoutPayloadsByOrderKey(
  entries: OrderKeyedPayload<CartCheckoutPayload>[]
): CartCheckoutPayload[] {
  const mergedByOrderKey = new Map<string, CartCheckoutPayload>()
  for (const { orderKey, payload } of entries) {
    const merged = mergedByOrderKey.get(orderKey)
    if (!merged) {
      mergedByOrderKey.set(orderKey, {
        productType: payload.productType,
        products: [...payload.products],
        orderTiles: [...payload.orderTiles],
        orderItems: [...payload.orderItems],
      })
    } else {
      merged.products.push(...payload.products)
      merged.orderTiles.push(...payload.orderTiles)
      merged.orderItems.push(...payload.orderItems)
    }
  }
  return [...mergedByOrderKey.values()]
}

// A rebuilt item differs from the stored one by its metadata.updatedAt stamp
// (Date.now() on every rebuild) and by the dismissals the store re-attaches;
// neither is content, and rewriting cartItems over them would churn
// persistence and every consumer once per rebuild.
function isSameCartItemContent(first: CartItem, second: CartItem): boolean {
  return comparer.structural(cartItemContent(first), cartItemContent(second))
}

function cartItemContent(item: CartItem): unknown {
  const { dismissedWarningTypes, ...content } = item
  const { updatedAt, ...metadata } = item.metadata
  return { ...content, metadata }
}
